import type { WRAccount, WRLoan, WROwner } from "../types/wealthReader";

/**
 * "Empresa" = titular de un activo. Puede ser persona física o jurídica.
 * Para agrupar usamos el primer owner con role `owner` de la cuenta. Si la
 * cuenta no tiene un owner explícito (no debería ocurrir), cae a "Sin titular".
 */
export type OwnerGroup = {
  ownerId: string;          // NIF/CIF/F-id sintético; key estable
  ownerName: string;        // mostrado en la UI
  isCompany: boolean;       // por heurística sobre el NIF/CIF
  accounts: WRAccount[];
  loans: WRLoan[];
};

export type ConsolidatedTotals = {
  /** Suma por divisa del campo balance.available (cuentas) y pending_amount (préstamos, negativo). */
  byCurrency: Record<string, { accountsAvailable: number; loansPending: number }>;
};

const NO_OWNER: WROwner = { ID: "", name: "(sin titular)", role: "owner" };

function pickAccountOwner(account: WRAccount): WROwner {
  const explicit = account.owners?.find((o) => o.role === "owner");
  if (explicit) return explicit;
  if (account.owners && account.owners.length > 0) return account.owners[0]!;
  return NO_OWNER;
}

function pickLoanHolder(loan: WRLoan): { id: string; name: string } {
  return loan.holders?.[0] ?? { id: "", name: "(sin titular)" };
}

/**
 * Heurística para distinguir empresa de persona física a partir del NIF/CIF español.
 * Empresas españolas: el primer carácter es una letra A-W (CIF). Personas físicas:
 * empieza con número (NIF) o letra X/Y/Z (NIE).
 * Como en los payloads de Wealth Reader pueden venir IDs sintéticos (`F005...`,
 * `J000...`), miramos también esos prefijos: `J` ≈ jurídica, `F` ≈ física.
 */
function looksLikeCompany(id: string): boolean {
  if (!id) return false;
  const first = id[0]!.toUpperCase();
  if (first === "J") return true;        // ID sintético jurídico de WR
  if (first === "F") return false;       // ID sintético físico de WR
  // CIF español empieza por A-W (excluyendo X/Y/Z que son NIE).
  if (/^[A-W]/.test(first)) return true;
  return false;
}

export function groupAccountsByOwner(
  accounts: WRAccount[],
  loans: WRLoan[],
): OwnerGroup[] {
  const map = new Map<string, OwnerGroup>();

  const ensureGroup = (id: string, name: string): OwnerGroup => {
    const key = id || name;
    const existing = map.get(key);
    if (existing) return existing;
    const created: OwnerGroup = {
      ownerId: id,
      ownerName: name,
      isCompany: looksLikeCompany(id),
      accounts: [],
      loans: [],
    };
    map.set(key, created);
    return created;
  };

  for (const account of accounts ?? []) {
    const owner = pickAccountOwner(account);
    const g = ensureGroup(owner.ID ?? "", owner.name);
    g.accounts.push(account);
  }
  for (const loan of loans ?? []) {
    const h = pickLoanHolder(loan);
    const g = ensureGroup(h.id, h.name);
    g.loans.push(loan);
  }

  // Empresas primero, luego personas; dentro de cada categoría alfabético.
  return Array.from(map.values()).sort((a, b) => {
    if (a.isCompany !== b.isCompany) return a.isCompany ? -1 : 1;
    return a.ownerName.localeCompare(b.ownerName, "es");
  });
}

export function consolidatedTotals(
  accounts: WRAccount[],
  loans: WRLoan[],
): ConsolidatedTotals {
  const byCurrency: ConsolidatedTotals["byCurrency"] = {};
  for (const a of accounts ?? []) {
    const cur = a.currency || "EUR";
    byCurrency[cur] ??= { accountsAvailable: 0, loansPending: 0 };
    byCurrency[cur].accountsAvailable += a.balances?.available ?? 0;
  }
  for (const l of loans ?? []) {
    const cur = l.currency || "EUR";
    byCurrency[cur] ??= { accountsAvailable: 0, loansPending: 0 };
    byCurrency[cur].loansPending += l.pending_amount ?? 0;
  }
  return { byCurrency };
}

/** Suma de saldos de un grupo, dividido por divisa. */
export function groupTotals(group: OwnerGroup): Record<string, number> {
  const out: Record<string, number> = {};
  for (const a of group.accounts) {
    const cur = a.currency || "EUR";
    out[cur] = (out[cur] ?? 0) + (a.balances?.available ?? 0);
  }
  return out;
}

const SUBTYPE_LABEL: Record<string, string> = {
  checking: "Cta. corriente",
  savings: "Ahorro",
  "line-of-credit": "Línea de crédito",
};

export function subtypeLabel(subtype: string | undefined): string {
  if (!subtype) return "—";
  return SUBTYPE_LABEL[subtype] ?? subtype;
}

export function formatMoney(value: number | undefined, currency = "EUR"): string {
  if (value === undefined || value === null) return "—";
  try {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

/** Formatea un IBAN con espacios cada 4 chars para mejor lectura. */
export function formatIban(code: string): string {
  if (!code) return "—";
  return code.replace(/(.{4})/g, "$1 ").trim();
}

/**
 * Convierte una fecha ISO (YYYY-MM-DD o timestamp) a dd/mm/aaaa. Devuelve
 * cadena vacía si la entrada es nula/vacía y la propia entrada si no puede
 * parsearla — así nunca oculta datos "raros" que vengan de SAP o WR.
 */
export function formatDateEs(input: string | null | undefined): string {
  if (!input) return "";
  const iso = input.slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return input;
  return `${m[3]}/${m[2]}/${m[1]}`;
}
